import { BackNextButton } from 'app/components/BackNextButton';
import React from 'react';
import styled from 'styled-components';
import { TextField } from 'app/components/TextField';
import {
  ApiError,
  GeneralLookup,
  LookupService,
  RegisterRider,
} from 'services/openapi';
import useApi from 'services/mavapi/useApi';
import AdminCustomButton from 'app/components/AdminCustomButton';

type Props = {
  onClickPlacementTwoBack?: () => void;
  handlePlacementSubmit?: (dataToSubmit?: RegisterRider) => void;
  riderData: RegisterRider;
  setRiderData: React.Dispatch<React.SetStateAction<RegisterRider>>;
};

const IndividualPlacementFormTwoNewRider = ({
  onClickPlacementTwoBack,
  handlePlacementSubmit,
  riderData,
  setRiderData,
}: Props) => {
  const [associationLookUp, setAssociationLookUp] =
    React.useState<GeneralLookup[]>();
  const { error: apiError, handleRequest } = useApi();
  const [, setError] = React.useState<ApiError | unknown>();
  const { lookupGetAllLookups } = LookupService;
  const [isButtonDisabled, setIsButtonDisabled] = React.useState(true);

  function getAllLookupData(names?: string) {
    handleRequest(lookupGetAllLookups(names))
      .then(data => {
        if (data) {
          data?.map(item => setAssociationLookUp(item?.values!));
        }
      })
      .catch(() => setError(apiError));
  }

  React.useEffect(() => {
    getAllLookupData('ASSOCIATIONS');
  }, []);

  const handleFieldChange = (index, field, value) => {
    setRiderData?.(prevData => {
      const updatedRiderAssociations = [...(prevData.riderAssociations || [])];
      const associationData = associationLookUp?.[index];

      updatedRiderAssociations[index] = {
        ...updatedRiderAssociations[index],
        [field]: value,
        association: {
          id: associationData?.id ?? '',
          name: associationData?.name ?? '',
          code: associationData?.code,
          children: associationData?.children,
          otherName: associationData?.otherName,
          isAdminLive: associationData?.isAdminLive,
        },
      };

      return { ...prevData, riderAssociations: updatedRiderAssociations };
    });
  };

  // Helper function to get rider association data by matching association ID
  const getRiderAssociationByIndex = (index: number) => {
    if (!riderData?.riderAssociations || !associationLookUp?.[index]) {
      return { name: '', idNumber: '' };
    }

    const associationId = associationLookUp[index].id;
    const matchingAssociation = riderData.riderAssociations.find(
      ra => ra?.association?.id === associationId
    );

    return {
      name: matchingAssociation?.name || '',
      idNumber: matchingAssociation?.idNumber || '',
    };
  };

  // Handle Skip button click - clear all associations and submit
  const handleSkipClick = () => {
    // Create updated rider data with empty associations
    const updatedRiderDataWithEmptyAssociations = {
      ...riderData,
      riderAssociations: [],
    };
    setRiderData?.(updatedRiderDataWithEmptyAssociations);
    // Pass the updated data directly to parent's submit handler
    handlePlacementSubmit?.(updatedRiderDataWithEmptyAssociations);
  };
  // Only validate, don't modify the data
  React.useEffect(() => {
    if (!riderData?.riderAssociations) {
      setIsButtonDisabled(true);
      return;
    }

    const areAllMappingsValid = riderData.riderAssociations.every(
      value =>
        (value?.name && value?.idNumber) || (!value?.name && !value?.idNumber),
    );

    setIsButtonDisabled(!areAllMappingsValid);
  }, [riderData?.riderAssociations]);

  return (
    <MainWrapper className="form-MainWrapper">
      <OverflowWrapper className="OverflowWrapper">
        <p className="heading-para">TO DOCUMENT PRIOR COMPETITION RECORDS:</p>
        <p className="para-two">
          <span>
            LIST YOUR NAME EXACTLY AS IT APPEARS IN ASSOCIATION RECORDS, AND{' '}
          </span>
          list your ID# for each association in which you have competed. (Not
          one in which you are simply a card holding member who has never
          participated in competition).
        </p>
        <MappedDataWrapper className="MappedDataWrapper">
          {associationLookUp?.map((value, index) => {
            const associationData = getRiderAssociationByIndex(index);

            return (
              <TextFieldGrid className="TextFieldGrid" key={index}>
                <p className="para-three">{value?.name}</p>
                <StyledNameField
                  value={associationData.name}
                  onChange={e => handleFieldChange(index, 'name', e.target.value)}
                  name={`name${index + 1}`}
                  placeholder="name"
                />

                <StyledIdField
                  value={associationData.idNumber}
                  onChange={e =>
                    handleFieldChange(index, 'idNumber', e.target.value)
                  }
                  name={`id${index + 1}`}
                  placeholder="ID number"
                />
              </TextFieldGrid>
            );
          })}
        </MappedDataWrapper>

        <ButtonWrapper className="bg-color-change">
          <AdminCustomButton
            buttonType="cancel"
            onClick={handleSkipClick}
          >
            Skip
          </AdminCustomButton>
          <BackNextButton
            classNameBack="btn-back"
            classNameNxt="btn-nxt"
            onClickNxt={() => handlePlacementSubmit?.()}
            onClickBack={onClickPlacementTwoBack}
            next="Pay"
            back="Back"
            disableNext={isButtonDisabled}
          />
        </ButtonWrapper>
      </OverflowWrapper>
    </MainWrapper>
  );
};
const MainWrapper = styled.div`
  position: relative;
  height: calc(100vh - 220px);
  width: 100%;
  .heading-para {
    color: #ff9900;
    font-size: 13px;
  }
  .para-two {
    color: #a7a7a7;
    font-size: 13px;
  }
  span {
    text-transform: uppercase;
  }
  @media (min-width: 576px) and (max-width: 767px) {
    .heading-para {
      font-size: 11px;
    }
    .para-two {
      font-size: 11px;
    }
  }
  @media (min-width: 768px) and (max-width: 991px) {
    .heading-para {
      font-size: 12px;
    }
    .para-two {
      font-size: 12px;
    }
  }
  @media (min-width: 992px) and (max-width: 1199px) {
    .heading-para {
      font-size: 13px;
    }
    .para-two {
      font-size: 13px;
    }
  }
`;
const OverflowWrapper = styled.div`
  height: 100%;
  width: 100%;
  overflow: auto;
  padding-bottom: 100px;
  ::-webkit-scrollbar {
    width: 5px;
    margin-left: 3px;
  }
  ::-webkit-scrollbar-track {
    background: transparent !important;
    border-radius: 10px;
  }
  ::-webkit-scrollbar-thumb {
    width: 5px;
    background: transparent !important;
    border-radius: 10px;
  }
`;
const MappedDataWrapper = styled.div`
  overflow: auto;
  height: calc(100vh - 225px);
  padding-bottom: 100px;
  ::-webkit-scrollbar {
    width: 5px;
    margin-left: 3px;
  }
  ::-webkit-scrollbar-track {
    background: transparent !important;
    border-radius: 10px;
  }
  ::-webkit-scrollbar-thumb {
    width: 5px;
    background: transparent !important;
    border-radius: 10px;
  }
`;
const TextFieldGrid = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  width: 100%;
  padding-bottom: 20px;
  .para-three {
    font-size: 11px;
    color: #9597a6;
    flex: 0 0 calc(33% - 20px);
  }
  label {
    display: none !important;
  }
`;
const StyledNameField = styled(TextField)`
  display: flex;
  align-items: center;
  margin: 0px;
  flex: 0 0 calc(33% - 20px);
  input {
    width: 100%;
    height: 30px;
  }
`;
const StyledIdField = styled(TextField)`
  display: flex;
  align-items: center;
  margin: 0px;
  flex: 0 0 calc(33% - 20px);
  input {
    width: 100%;
    height: 30px;
  }
  ::placeholder {
  }
`;
const ButtonWrapper = styled.div`
  height: 70px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px 0px 20px;

  &.bg-color {
    background: rgb(42 42 42) 100%;
  }
  &.bg-color-change {
    position: absolute;
    bottom: -3px;
    left: 0px;
    background: linear-gradient(
      to bottom,
      rgb(36 36 36 / 27%) 0%,
      rgb(42 42 42) 100%
    );
    border: 1px solid rgb(42 42 42);
    backdrop-filter: blur(5px);
  }
  @media (min-width: 992px) and (max-width: 1199px) {
    height: 50px;
  }
  @media (min-width: 1200px) and (max-width: 1365px) {
    height: 55px;
  }
  @media (min-width: 1366px) and (max-width: 1448px) {
    height: 70px;
  }
`;

export default IndividualPlacementFormTwoNewRider;
